import { PendingOperationEntry } from '../types/patchTypes';

/**
 * One submission's worth of work: a set of patches that all receive exactly the same operations.
 *
 * The bulk pricing API takes a single flat `{ patchIds, setOperations, removeOperations }` and applies
 * every operation to every patch, so store-level scoping has to be expressed as several requests.
 * Patches whose effective operations are identical are merged into one group to keep that count down.
 */
export interface OperationPlanGroup {
  patchIds: string[];
  setOperations: PendingOperationEntry[];
  removeOperations: PendingOperationEntry[];
}

/** An all-store operation that a direct, store-level edit held back for one patch. */
export interface OperationSuppression {
  conflictKey: string;
  patchId: string;
  storeNumber: string;
  /** The all-store operation that will not reach this patch. */
  suppressedOpId: string;
  /** The store-level operation that took its place. */
  winningOpId: string;
}

export interface OperationPlan {
  groups: OperationPlanGroup[];
  suppressions: OperationSuppression[];
}

export interface PendingOperations {
  setOperations?: PendingOperationEntry[];
  removeOperations?: PendingOperationEntry[];
}

type Bucket = 'set' | 'remove';

interface Tagged {
  entry: PendingOperationEntry;
  bucket: Bucket;
}

const tag = (entries: PendingOperationEntry[] | undefined, bucket: Bucket): Tagged[] =>
  (entries || []).filter((entry) => entry && entry.scope).map((entry) => ({ entry, bucket }));

/**
 * Every patch any of these pending operations reaches, deduplicated.
 *
 * For reporting and for the publish step - which needs to know what was touched - not for building
 * requests. Requests come from `resolveOperationPlan`, because this union deliberately loses the
 * per-operation scoping that makes store-level edits work.
 */
export const collectPatchIds = ({ setOperations, removeOperations }: PendingOperations): string[] => {
  const patchIds = new Set<string>();

  [...(setOperations || []), ...(removeOperations || [])].forEach((entry) => {
    if (!entry?.scope) return;
    if (entry.scope.kind === 'store') patchIds.add(entry.scope.patchId);
    else entry.scope.patchIds?.forEach((patchId) => patchIds.add(patchId));
  });

  return Array.from(patchIds);
};

/**
 * Splits pending operations into the smallest set of API requests that honours store-level overrides.
 *
 * Scoping is resolved per **patch**, not per store. A store gets one patch per patch type, so keying
 * on patchId gives the intended "only this store" behaviour while also keeping a direct edit on a
 * store's Product Price patch from suppressing an all-store operation on that same store's Bundle
 * Price patch.
 *
 * For each patch, the effective operations are its own direct edits plus every all-store operation
 * whose target that patch has not been directly edited for. Suppression is by conflict key and
 * ignores set-versus-remove, so a store-level delete holds back an all-store set on the same target -
 * see getOperationConflictKey.
 */
export const resolveOperationPlan = ({ setOperations, removeOperations }: PendingOperations): OperationPlan => {
  const tagged = [...tag(setOperations, 'set'), ...tag(removeOperations, 'remove')];

  const storeScoped = tagged.filter(({ entry }) => entry.scope.kind === 'store');
  const allScoped = tagged.filter(({ entry }) => entry.scope.kind === 'all');

  // Which targets each patch has been directly edited for, and which edit claimed each target first.
  const directEditsByPatch = new Map<string, Map<string, PendingOperationEntry>>();
  storeScoped.forEach(({ entry }) => {
    const scope = entry.scope as { kind: 'store'; storeNumber: string; patchId: string };
    let byKey = directEditsByPatch.get(scope.patchId);
    if (!byKey) {
      byKey = new Map();
      directEditsByPatch.set(scope.patchId, byKey);
    }
    if (!byKey.has(entry.conflictKey)) byKey.set(entry.conflictKey, entry);
  });

  // Every patch any pending operation could reach.
  const patchIds = new Set<string>();
  storeScoped.forEach(({ entry }) => {
    patchIds.add((entry.scope as { patchId: string }).patchId);
  });
  allScoped.forEach(({ entry }) => {
    (entry.scope as { patchIds: string[] }).patchIds?.forEach((patchId) => patchIds.add(patchId));
  });

  const suppressions: OperationSuppression[] = [];
  // Signature -> the patches sharing it, and the operations they all receive.
  const bySignature = new Map<string, { patchIds: string[]; operations: Tagged[] }>();

  Array.from(patchIds)
    .sort()
    .forEach((patchId) => {
      const directEdits = directEditsByPatch.get(patchId);

      const effective: Tagged[] = storeScoped.filter(
        ({ entry }) => (entry.scope as { patchId: string }).patchId === patchId
      );

      allScoped.forEach((candidate) => {
        const { entry } = candidate;
        const scope = entry.scope as { kind: 'all'; patchIds: string[] };
        if (!scope.patchIds?.includes(patchId)) return;

        const winner = directEdits?.get(entry.conflictKey);
        if (winner) {
          const winnerScope = winner.scope as { storeNumber: string };
          suppressions.push({
            conflictKey: entry.conflictKey,
            patchId,
            storeNumber: winnerScope.storeNumber,
            suppressedOpId: entry.opId,
            winningOpId: winner.opId,
          });
          return;
        }

        effective.push(candidate);
      });

      if (!effective.length) return;

      const signature = effective
        .map(({ entry }) => entry.opId)
        .sort()
        .join('|');

      const group = bySignature.get(signature);
      if (group) {
        group.patchIds.push(patchId);
      } else {
        bySignature.set(signature, { patchIds: [patchId], operations: effective });
      }
    });

  const groups: OperationPlanGroup[] = Array.from(bySignature.values())
    .map(({ patchIds: groupPatchIds, operations }) => ({
      patchIds: groupPatchIds,
      setOperations: operations.filter(({ bucket }) => bucket === 'set').map(({ entry }) => entry),
      removeOperations: operations.filter(({ bucket }) => bucket === 'remove').map(({ entry }) => entry),
    }))
    // Broadest job first, so the bulk of the work is submitted before the per-store exceptions.
    // patchIds is the tie-break only to keep the order stable for tests and for progress display.
    .sort((a, b) => b.patchIds.length - a.patchIds.length || a.patchIds[0].localeCompare(b.patchIds[0]));

  return { groups, suppressions };
};
